import "./ui/base.css";
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";
import { effect } from "@preact/signals";
import {
  type AtmosphereRating,
  canLook,
  followLoading,
  prepareToLook,
  rateAtmosphere,
} from "./atmosphere.ts";
import {
  type Appearance,
  appearanceLabel,
  appearanceMaterial,
  DEFAULT_APPEARANCES,
  glowsInKiln,
  MATERIAL_NAMES,
  requiresKiln,
  shrinksInKiln,
} from "./appearance.ts";
import { followBack } from "./back.ts";
import {
  Clay,
  CLAY_VOLUME,
  type ClayKind,
  type ClaySnapshot,
  type Hit,
  type Hole,
  type HoleFacing,
  mirrorHit,
  type Repeat,
  type Tool,
} from "./clay.ts";
import { faceImage } from "./face.ts";
import { computeStats, type IndexedMesh, meshSdf } from "./geometry.ts";
import { Judge, type Judgement, type Shape } from "./judge.ts";
import { clayMesh, targetMeshes } from "./meshes.ts";
import { Morph } from "./morph.ts";
import { loadRecords, recordDelivery } from "./records.ts";
import {
  accessTo,
  challengeFor,
  CHALLENGES,
  cleared,
  freeKneading,
  mastered,
  progressSummary,
  type Records,
} from "./progression.ts";
import { type RequestDef, REQUESTS } from "./requests.ts";
import { drawPhoto, drawSlip, freeSlipOf, slipOf } from "./slip.ts";
import { closeness, Scorer } from "./score.ts";
import { keepPiece, Shelf } from "./shelf.ts";
import { setTune, setVolumes, sounds } from "./sound.ts";
import { rateTags, type Tags } from "./tags.ts";
import { Timelapse } from "./timelapse.ts";
import { clearWork, loadWork, saveMeta, saveStep, type Work, type WorkMeta } from "./work.ts";
import { Workshop } from "./workshop.ts";
import { Target, TARGET_DEFS } from "./targets.ts";
import { letterThumbs, mountBoard } from "./ui/board.tsx";
import { mountAsk } from "./ui/ask.tsx";
import { mountNews, type Opened } from "./ui/news.tsx";
import { clayName, mountClayChooser } from "./ui/clays.tsx";
import { mountCredits } from "./ui/credits.tsx";
import { mountLedger } from "./ui/ledger.tsx";
import { type ClayColor, mountSettings, saveSettings, settings } from "./ui/settings.tsx";
import { delivery, mountDelivery, SLIP_IN } from "./ui/delivery.tsx";
import {
  kneading,
  markHelpSeen,
  mountKneading,
  showHelp,
  showHelpOnce,
  targetView,
} from "./ui/kneading.tsx";
import {
  hasTutorial,
  skipTutorial,
  startTutorial,
  tutorial,
  tutorialGuide,
} from "./ui/tutorial.tsx";
import {
  closeIris,
  lowerKiln,
  mountOverlays,
  openIris,
  overlays,
  raiseKiln,
} from "./ui/overlays.tsx";
import { type Looking, mountResult, result } from "./ui/result.tsx";
import { mountStart, start } from "./ui/start.tsx";
import { mountTitle, title, titlePane } from "./ui/title.tsx";

const SCORE_INTERVAL_MS = 250;
/**
 * Vague hints shown while kneading, by hidden closeness (0 at the start, 1 perfect).
 * Three quarters of the way is about where a piece with its feature in place ranks A.
 */
const MOODS: [number, string][] = [
  [0.75, "これは…できたかも！"],
  [0.55, "かなり近い気がする！"],
  [0.35, "いい感じかも…！"],
  [0.12, "形が見えてきた…？"],
  [0, "こねこね…"],
];
const CAMERA_DISTANCE = 6.5;
/**
 * The clay while it is kneaded, by the player's choice (see ui/settings.tsx), and where the
 * brush works glowing on it (see tintBrush): one colour for every tool, far from the clay's
 * own (the rings' colours for push and inflate hardly show on the brown). The gray is lit
 * as a sculpting program shows its clay: little light from the room and little sheen, so
 * it darkens towards its outline, and a little blue in it keeps it neutral under the warm
 * key light.
 */
const CLAY_COLORS: Record<
  ClayColor,
  {
    color: number;
    sheen: number;
    sheenColor: number;
    /** How much of the scene's light from the room it takes. */
    environment: number;
    tint: number;
  }
> = {
  brown: { color: 0xf2b48c, sheen: 0.8, sheenColor: 0xffd6bd, environment: 1, tint: 0x4fb4ff },
  gray: { color: 0xe6eefb, sheen: 0.2, sheenColor: 0xd8d8d8, environment: 0.05, tint: 0xff7a1a },
};
const tintColor = new THREE.Color();
const CURSOR_COLORS: Record<Tool, number> = {
  grab: 0xffffff,
  push: 0xffb09a,
  inflate: 0xffdc7a,
  smooth: 0xa6ddff,
  flatten: 0xc9b8ff,
};
const TOOL_KEYS: Record<string, Tool> = {
  1: "grab",
  2: "push",
  3: "inflate",
  4: "smooth",
  5: "flatten",
};

const $ = <T extends HTMLElement>(selector: string) => document.querySelector<T>(selector)!;
const canvas = $<HTMLCanvasElement>("#scene");

// ---------- Renderer & scenes ----------

const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
renderer.toneMapping = THREE.NeutralToneMapping;
const environment =
  new THREE.PMREMGenerator(renderer).fromScene(new RoomEnvironment(), 0.04).texture;

const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 60);
camera.position.set(0, 1.2, CAMERA_DISTANCE);
const keyLight = new THREE.DirectionalLight(0xfff4e8, 1.4);
keyLight.position.set(2, 3, 1);
camera.add(keyLight);

const scene = new THREE.Scene();
scene.environment = environment;
scene.environmentIntensity = 0.8;
scene.add(camera);

const clayMaterial = new THREE.MeshPhysicalMaterial({
  color: 0xf2b48c,
  roughness: 0.55,
  sheen: 0.8,
  sheenRoughness: 0.45,
  sheenColor: new THREE.Color(0xffd6bd),
  clearcoat: 0.2,
  clearcoatRoughness: 0.5,
});
/**
 * Each kind of clay, as it starts with its holes facing each way a target asks for, once
 * made ready (see loadClay).
 */
const clays = new Map<string, Clay>();
/** The clay of the current stage; each stage says which kind it starts from. */
let clay!: Clay;

/** Makes the clay of `kind` ready, its mesh made in the meshing worker (see meshes.ts). */
async function loadClay(kind: ClayKind, holes: HoleFacing = "front"): Promise<Clay> {
  const key = `${kind} ${holes}`;
  let c = clays.get(key);
  if (!c) {
    const mesh = await clayMesh(kind, holes);
    // Another wait for the same clay may have made it meanwhile.
    c = clays.get(key) ?? new Clay(clayMaterial, mesh);
    clays.set(key, c);
  }
  return c;
}

/** Puts the clay of `kind` out, once made ready (see loadClay). */
function useClay(kind: ClayKind, holes: HoleFacing = "front"): void {
  const next = clays.get(`${kind} ${holes}`)!;
  if (next === clay) return;
  if (clay) scene.remove(clay.mesh);
  clay = next;
  scene.add(clay.mesh);
}

function shapeOf({ positions, index }: IndexedMesh): Shape {
  const n = positions.length / 3;
  const areas = new Float32Array(n);
  const { centroid } = computeStats(positions, index, new Float32Array(n * 3), areas);
  return { positions, index, areas, centroid };
}

function createShadowTexture(): THREE.Texture {
  const size = 128;
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const g = c.getContext("2d")!;
  const gradient = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  gradient.addColorStop(0, "rgba(110, 60, 30, 0.32)");
  gradient.addColorStop(1, "rgba(110, 60, 30, 0)");
  g.fillStyle = gradient;
  g.fillRect(0, 0, size, size);
  const texture = new THREE.CanvasTexture(c);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}
const shadow = new THREE.Mesh(
  new THREE.PlaneGeometry(1, 1),
  new THREE.MeshBasicMaterial({
    map: createShadowTexture(),
    transparent: true,
    depthWrite: false,
    toneMapped: false,
  }),
);
shadow.rotation.x = -Math.PI / 2;
shadow.scale.setScalar(3.6);
scene.add(shadow);

/** Result screen: the clay melting into the target ("あなた ↔ おてほん"). */
const TARGET_COLOR = new THREE.Color(0x7fcab6);
const morphMaterial = clayMaterial.clone();
const morphMesh = new THREE.Mesh(new THREE.BufferGeometry(), morphMaterial);
morphMesh.visible = false;
scene.add(morphMesh);
let morph: Morph | null = null;
let morphShown = 0;

/** Result screen: the judgement shown, for the delivery slip. */
let judgement: Judgement | null = null;
/** The commemorative photo (the stamped slip and the score card) as a PNG to save or share. */
let slipFile: File | null = null;

/** A temporary copy of the selected appearance, with a kiln glow while soil or pottery cools. */
const deliveryMaterial = clayMaterial.clone();
deliveryMaterial.emissive.set(0xff6a2b);
const FIRED_SCALE = 0.93;
/**
 * Delivery timing (ms): soil, pottery, and bread in the kiln; the other materials behind the iris;
 * the fired glow cooling, the piece moving aside for the slip, and the slip at rest a moment
 * before the shop stamps it.
 */
const DELIVERY = { fire: 1600, set: 1300, cool: 1400, aside: SLIP_IN.duration, rest: 400 };
let delivered = false;
/** Size of the piece on show (1, or smaller once fired). */
let pieceScale = 1;
/** Where the shadow lies under the clay being kneaded, while the piece on show stands up. */
let shadowRest = 0;
/** When the fired piece came out (it glows, cooling), and when it started moving aside. */
let firedAt: number | null = null;
let asideAt: number | null = null;

/** Commemorative photos: front and back three-quarter views, lit from the camera. */
const PHOTO_VIEWS = [new THREE.Vector3(0.6, 0.45, 1), new THREE.Vector3(-0.6, 0.45, -1)].map(
  (v) => v.normalize(),
);
const photoCamera = new THREE.PerspectiveCamera(30, 1, 0.1, 60);
const photoLight = keyLight.clone();
photoCamera.add(photoLight);

/** Result screen: the kneading so far, one frame per step, to scrub through. */
const timelapse = new Timelapse();
const timelapseMesh = new THREE.Mesh(new THREE.BufferGeometry(), clayMaterial);
timelapseMesh.visible = false;
scene.add(timelapseMesh);
/** Kneading steps still in effect (strokes and resets that were not undone). */
let historyDepth = 0;

/** Auto-play after submitting: wait, melt into the target, hold, come back (ms). */
const MORPH_PLAY = { wait: 600, melt: 1400, hold: 1000, back: 1000 };
type PlayPhase = "wait" | "melt" | "hold" | "back" | "done";
let morphPlay: { start: number; phase: PlayPhase } | null = null;
/** "ぷるん": a decaying squash-and-stretch after the morph arrives somewhere. */
let wobbleStart: number | null = null;

const easeInOut = (x: number) => (x < 0.5 ? 4 * x * x * x : 1 - (-2 * x + 2) ** 3 / 2);

function morphPlayAt(ms: number): { t: number; phase: PlayPhase } {
  const { wait, melt, hold, back } = MORPH_PLAY;
  if (ms < wait) return { t: 0, phase: "wait" };
  ms -= wait;
  if (ms < melt) return { t: easeInOut(ms / melt), phase: "melt" };
  ms -= melt;
  if (ms < hold) return { t: 1, phase: "hold" };
  ms -= hold;
  if (ms < back) return { t: 1 - easeInOut(ms / back), phase: "back" };
  return { t: 0, phase: "done" };
}

const ghost = new THREE.Mesh(
  new THREE.BufferGeometry(),
  new THREE.MeshStandardMaterial({
    color: 0x5fb7a2,
    roughness: 0.4,
    transparent: true,
    opacity: 0.22,
    depthWrite: false,
  }),
);
ghost.renderOrder = 1;
ghost.visible = false;
scene.add(ghost);

const cursorMaterial = new THREE.MeshBasicMaterial({
  transparent: true,
  opacity: 0.85,
  depthTest: false,
  side: THREE.DoubleSide,
  toneMapped: false,
});
/** Brush rings: the pointer's, and its mirror image in symmetry mode. */
const cursors = [0, 1].map(() => {
  const ring = new THREE.Mesh(new THREE.RingGeometry(0.95, 1, 72), cursorMaterial);
  ring.renderOrder = 2;
  ring.visible = false;
  scene.add(ring);
  return ring;
});
/** On the wheel: the ring the brush works all the way round, at the brush's height. */
const wheelGuide = new THREE.LineLoop(
  new THREE.BufferGeometry().setFromPoints(
    Array.from({ length: 96 }, (_, k) => {
      const a = (2 * Math.PI * k) / 96;
      return new THREE.Vector3(Math.cos(a), 0, Math.sin(a));
    }),
  ),
  new THREE.LineBasicMaterial({ transparent: true, opacity: 0.7, depthTest: false }),
);
wheelGuide.renderOrder = 2;
wheelGuide.visible = false;
scene.add(wheelGuide);

const LESSON_COLOR = 0xd9774f;
const lessonRing = new THREE.RingGeometry(0.88, 1, 72);
const lessonMaterial = new THREE.MeshBasicMaterial({
  color: LESSON_COLOR,
  transparent: true,
  opacity: 0.9,
  depthTest: false,
  side: THREE.DoubleSide,
  toneMapped: false,
});
/** The lesson's marks on the clay (see placeLessonMarks). */
const lessonMarks = new THREE.Group();
scene.add(lessonMarks);

/** A potter's wheel head: a metal disc with a few rings and marks, so its turning shows. */
function createTurntable(): THREE.Mesh {
  const size = 256;
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const g = c.getContext("2d")!;
  g.fillStyle = "#b9c3c9";
  g.fillRect(0, 0, size, size);
  g.strokeStyle = "rgba(70, 85, 95, 0.35)";
  g.lineWidth = 3;
  for (const r of [0.25, 0.55, 0.85]) {
    g.beginPath();
    g.arc(size / 2, size / 2, (r * size) / 2, 0, 2 * Math.PI);
    g.stroke();
  }
  g.lineWidth = 6;
  for (const a of [0, Math.PI]) {
    g.beginPath();
    g.moveTo(size / 2 + Math.cos(a) * size * 0.3, size / 2 + Math.sin(a) * size * 0.3);
    g.lineTo(size / 2 + Math.cos(a) * size * 0.45, size / 2 + Math.sin(a) * size * 0.45);
    g.stroke();
  }
  const top = new THREE.CanvasTexture(c);
  top.colorSpace = THREE.SRGBColorSpace;
  // Transparent throughout, so it can fade when seen from below (see turnWheel).
  const metal = { metalness: 0.6, roughness: 0.45, transparent: true };
  const disc = new THREE.Mesh(new THREE.CylinderGeometry(1.5, 1.45, 0.12, 64), [
    new THREE.MeshStandardMaterial({ color: 0x8e9aa2, ...metal }),
    new THREE.MeshStandardMaterial({ map: top, ...metal }),
    new THREE.MeshStandardMaterial({ color: 0x8e9aa2, ...metal }),
  ]);
  disc.visible = false;
  return disc;
}
/** The wheel under the clay while it is on; its top follows the clay's underside. */
const turntable = createTurntable();
scene.add(turntable);
/** How fast the wheel turns (radians per second). */
const TURNTABLE_SPEED = 3;
/**
 * How far the wheel, and the clay on it, has turned (0 to 2π). The turn is only shown: a
 * stroke on the wheel works all the way round, so it comes out the same however far the
 * clay has turned, and the clay is judged as it stands at 0.
 */
let spin = 0;
const _turned = new THREE.Matrix4();

const previewScene = new THREE.Scene();
previewScene.environment = environment;
previewScene.environmentIntensity = 0.8;
const previewCamera = new THREE.PerspectiveCamera(30, 1, 0.1, 60);
const previewLight = keyLight.clone();
previewCamera.add(previewLight);
previewScene.add(previewCamera);
const previewMesh = new THREE.Mesh(
  new THREE.BufferGeometry(),
  new THREE.MeshPhysicalMaterial({
    color: 0x7fcab6,
    roughness: 0.5,
    sheen: 0.6,
    sheenColor: new THREE.Color(0xd8fff4),
    clearcoat: 0.2,
  }),
);
previewScene.add(previewMesh);
let previewDistance = 6;
/** Main camera distance multiplier for the current stage's size. */
let stageScale = 1;

// ---------- Camera controls & input ----------

const raycaster = new THREE.Raycaster();
const pointer = new THREE.Vector2();
const downPointers = new Set<number>();
let strokePointer: number | null = null;
let strokeTool: Tool | null = null;
/** The stroke was begun looking through a hole: the tool works the hole. */
let strokeOnHole = false;
let strokeStartVersion = 0;
/** When the stroke began (event time), and where its pointer is now and what it is. */
let strokeStartTime = 0, strokeX = 0, strokeY = 0, strokeByTouch = false;
/**
 * A second finger down this soon (ms) after a stroke began under the first means the two were
 * put down together to turn and zoom the view: the stroke is taken back (see pinchInstead).
 */
const PINCH_GRACE = 300;
/** The first finger's press, passed on again to the camera controls (see pinchInstead). */
let replayingPress = false;
/** Strokes the current one counts for: 2 in symmetry mode (it kneads both sides). */
let strokeCost = 1;
let strokes = 0;
/** Symmetry mode: every stroke also kneads its mirror image across the target's plane. */
let symmetric = false;
/** The potter's wheel: every stroke works all the way round the upright axis. */
let wheel = false;
/** Strokes one stroke on the wheel counts for: about a ring kneaded by hand, symmetrically. */
const WHEEL_STROKES = 4;

function strokeRepeat(): Repeat {
  if (wheel) return "wheel";
  return symmetric ? { mirror: mirrorAxis() } : null;
}

const UNDO_LIMIT = 30;
interface UndoStep {
  clay: ClaySnapshot;
  strokes: number;
}
let undoStack: UndoStep[] = [];
/** Taken when a stroke starts; kept only if the stroke changed the clay. */
let pendingUndo: UndoStep | null = null;
/** After submitting, the clay is locked and the result can be inspected. */
let judged = false;
let hovering = false;
let currentTool: Tool = "grab";
let shiftHeld = false;
let ctrlHeld = false;
/** Brush radius as a fraction of the shorter side of the screen. */
let brushSize = kneading.brush.value;

function updatePointer(e: PointerEvent): void {
  const rect = canvas.getBoundingClientRect();
  pointer.set(
    ((e.clientX - rect.left) / rect.width) * 2 - 1,
    -((e.clientY - rect.top) / rect.height) * 2 + 1,
  );
}

function castPointer(): Hit | null {
  raycaster.setFromCamera(pointer, camera);
  if (spin === 0) return clay.raycast(raycaster.ray);
  // On the turning wheel: cast in the clay's own frame, and turn the hit back out.
  const hit = clay.raycast(raycaster.ray.clone().applyMatrix4(_turned.makeRotationY(-spin)));
  if (!hit) return null;
  _turned.makeRotationY(spin);
  hit.point.applyMatrix4(_turned);
  hit.normal.applyMatrix4(_turned);
  return hit;
}

/**
 * The hole the pointer looks through (when it is not on the clay), where the tools work the
 * hole; none on the wheel, whose strokes go all the way round the clay.
 */
function holeAtPointer(): Hole | null {
  if (wheel || spin !== 0) return null;
  raycaster.setFromCamera(pointer, camera);
  return clay.holeAt(raycaster.ray);
}

/** Brings the clay straight back to the front (0), the wheel with it. */
function stopSpin(): void {
  spin = 0;
  clay.mesh.rotation.y = turntable.rotation.y = 0;
}

const _toPoint = new THREE.Vector3();
const _viewDir = new THREE.Vector3();

/**
 * World-space brush radius at `point` for a brush of constant on-screen size, so zooming
 * in gives a finer brush and zooming out a broader one.
 */
function brushRadiusAt(point: THREE.Vector3): number {
  const depth = _toPoint.subVectors(point, camera.position).dot(camera.getWorldDirection(_viewDir));
  const viewHeight = 2 * depth * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
  const pixels = brushSize * Math.min(canvas.clientWidth, canvas.clientHeight);
  return pixels * viewHeight / canvas.clientHeight;
}

function toolFor(e: { shiftKey: boolean; ctrlKey: boolean; metaKey: boolean }): Tool {
  if (e.shiftKey) return "push";
  if (e.ctrlKey || e.metaKey) return "inflate";
  return currentTool;
}

function stepStroke(dt: number): void {
  const hit = castPointer();
  // Grab keeps the size it started with, as does a stroke on a hole; the other tools follow
  // the zoom under the pointer.
  if (hit && strokeTool !== "grab" && !strokeOnHole) clay.brushRadius = brushRadiusAt(hit.point);
  clay.dragStroke(raycaster.ray, hit, dt);
}

// Registered before OrbitControls so a press on the clay can disable orbiting first.
canvas.addEventListener("pointerdown", (e) => {
  if (replayingPress) return;
  const othersDown = downPointers.size > 0;
  downPointers.add(e.pointerId);
  if (othersDown && strokeByTouch && strokePointer !== null && e.pointerType === "touch") {
    pinchInstead(e);
    return;
  }
  if (othersDown || e.button !== 0 || judged || shelf.isOpen || atTitle) return;
  // Off the wheel, strokes work the clay as it is judged: facing the front.
  if (!wheel) stopSpin();
  updatePointer(e);
  const hit = castPointer();
  const at = hit ?? holeAtPointer();
  if (!at) return; // background: OrbitControls rotates the view
  controls.enabled = false;
  strokePointer = e.pointerId;
  canvas.setPointerCapture(e.pointerId);
  strokeTool = toolFor(e);
  strokeOnHole = !hit;
  strokeStartVersion = clay.version;
  strokeStartTime = e.timeStamp;
  strokeX = e.clientX;
  strokeY = e.clientY;
  strokeByTouch = e.pointerType === "touch";
  pendingUndo = { clay: clay.snapshot(), strokes };
  clay.brushRadius = brushRadiusAt(at.point);
  const viewDir = camera.getWorldDirection(new THREE.Vector3());
  clay.beginStroke(strokeTool, at, viewDir, strokeRepeat());
  // A hole's mirror image is worked only if the clay has a hole there too.
  strokeCost = wheel ? WHEEL_STROKES : clay.mirrored ? 2 : 1;
  setCursorColor(strokeTool);
  sounds.press(strokeTool);
  squishTravel = 0;
  squishX = e.clientX;
  squishY = e.clientY;
});

canvas.addEventListener("pointermove", (e) => {
  if (strokePointer !== null ? e.pointerId === strokePointer : e.pointerType === "mouse") {
    updatePointer(e);
    hovering = e.pointerType === "mouse";
  }
  if (e.pointerId === strokePointer) {
    strokeX = e.clientX;
    strokeY = e.clientY;
    squishAlong(e);
  }
});

/**
 * A second finger down while the first kneads: the two turn and zoom the view, as they do
 * put down on the background. The stroke ends there, or is taken back if it had only just
 * begun (the fingers were meant to come down together), and the camera controls, which the
 * stroke kept from hearing of the first finger, are told of it now; `second` reaches them
 * next, and with two fingers they pinch.
 */
function pinchInstead(second: PointerEvent): void {
  const first = strokePointer!;
  if (second.timeStamp - strokeStartTime < PINCH_GRACE) takeBackStroke();
  else endStroke();
  replayingPress = true;
  canvas.dispatchEvent(
    new PointerEvent("pointerdown", {
      pointerId: first,
      pointerType: "touch",
      isPrimary: true,
      clientX: strokeX,
      clientY: strokeY,
      button: 0,
      buttons: 1,
    }),
  );
  replayingPress = false;
}

/** The clay squishes softly every so far (CSS pixels) the brush is moved in a stroke. */
const SQUISH_EVERY = 40;
/** How far the pointer has moved since the clay last squished, and where it was then. */
let squishTravel = 0, squishX = 0, squishY = 0;

function squishAlong(e: PointerEvent): void {
  squishTravel += Math.hypot(e.clientX - squishX, e.clientY - squishY);
  squishX = e.clientX;
  squishY = e.clientY;
  if (squishTravel < SQUISH_EVERY || !strokeTool) return;
  squishTravel = 0;
  sounds.knead(strokeTool);
}

function releasePointer(e: PointerEvent): void {
  downPointers.delete(e.pointerId);
  if (e.pointerId !== strokePointer) return;
  // Apply movement that arrived after the last frame (a quick flick can start and end
  // between two frames).
  updatePointer(e);
  stepStroke(0);
  endStroke();
}

function endStroke(): void {
  if (strokePointer === null) return;
  if (canvas.hasPointerCapture(strokePointer)) canvas.releasePointerCapture(strokePointer);
  strokePointer = null;
  const completedTool = strokeTool!;
  strokeTool = null;
  const changed = clay.version !== strokeStartVersion;
  if (changed) {
    strokes += strokeCost;
    if (!freeClay) kneading.strokeBrush.value = clay.brushRadius / stage.target.scale;
    kneading.strokeTool.value = completedTool;
    kneading.strokeOnHole.value = strokeOnHole;
    kneading.strokes.value = strokes;
    if (pendingUndo) pushUndo(pendingUndo);
    sounds.counted();
  }
  pendingUndo = null;
  clay.endStroke();
  if (changed) recordStep(historyDepth + 1);
  controls.enabled = true;
  updateScore(performance.now(), true);
}

/** Ends the stroke under way as if it had never begun: the clay as it was, nothing counted. */
function takeBackStroke(): void {
  if (strokePointer === null) return;
  if (canvas.hasPointerCapture(strokePointer)) canvas.releasePointerCapture(strokePointer);
  strokePointer = null;
  strokeTool = null;
  clay.restore(pendingUndo!.clay);
  pendingUndo = null;
  controls.enabled = true;
  updateScore(performance.now(), true);
}

canvas.addEventListener("pointerup", releasePointer);
canvas.addEventListener("pointercancel", releasePointer);
canvas.addEventListener("pointerleave", (e) => {
  if (e.pointerType === "mouse") hovering = false;
});
window.addEventListener("blur", () => {
  downPointers.clear();
  endStroke();
  shiftHeld = ctrlHeld = false;
  refreshTools();
});

const controls = new OrbitControls(camera, canvas);
controls.enablePan = false;
controls.enableDamping = true;
controls.minDistance = 3.5;
controls.maxDistance = 16;
controls.mouseButtons = {
  LEFT: THREE.MOUSE.ROTATE,
  MIDDLE: THREE.MOUSE.DOLLY,
  RIGHT: THREE.MOUSE.ROTATE,
};
controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_ROTATE };
// Off on the title screen; the delivered piece turns on show at this speed (see deliver).
controls.enabled = false;
controls.autoRotateSpeed = 1.5;

// ---------- UI ----------

function setCursorColor(tool: Tool): void {
  cursorMaterial.color.setHex(CURSOR_COLORS[tool]);
}

/**
 * Notes that `depth` steps are now in effect, recording the shape for the timelapse and
 * keeping the work for a reload (once a request is taken on: the title only prepares one).
 */
function recordStep(depth: number): void {
  historyDepth = depth;
  const frame = timelapse.record(depth, strokes, clay.rest, clay.index);
  if (atTitle) return;
  const step = { step: depth, strokes, clay: clay.snapshot() };
  saveStep(workMeta(), step, frame, timelapse.steps, depth - undoStack.length);
}

function workMeta(): WorkMeta {
  const kneaded = freeClay ? FREE + freeClay : request.id;
  return {
    request: kneaded,
    symmetric,
    wheel,
    tool: currentTool,
    brush: brushSize,
    paused: atTitle,
  };
}

/** Keeps the tools' settings with the work, once a request is taken on. */
function keepSettings(): void {
  if (!atTitle) saveMeta(workMeta());
}

function pushUndo(step: UndoStep): void {
  undoStack.push(step);
  if (undoStack.length > UNDO_LIMIT) undoStack.shift();
  kneading.canUndo.value = true;
}

function undo(): void {
  if (judged || undoStack.length === 0) return;
  endStroke();
  const step = undoStack.pop()!;
  sounds.undo();
  clay.restore(step.clay);
  strokes = step.strokes;
  kneading.strokes.value = strokes;
  kneading.canUndo.value = undoStack.length > 0;
  recordStep(historyDepth - 1);
  updateScore(performance.now(), true);
}

/** Back to the starting clay; unlike retrying, this can be undone. */
function resetClay(): void {
  endStroke();
  pushUndo({ clay: clay.snapshot(), strokes });
  sounds.restart();
  clay.reset();
  strokes = 0;
  kneading.strokes.value = 0;
  recordStep(historyDepth + 1);
  setJudged(false);
  updateScore(performance.now(), true);
}

function setSymmetric(value: boolean): void {
  symmetric = value;
  kneading.symmetric.value = value;
  // The wheel works all the way round already: one or the other.
  if (value && wheel) setWheel(false);
  keepSettings();
}

function setWheel(value: boolean): void {
  wheel = value;
  kneading.wheel.value = value;
  if (value && symmetric) setSymmetric(false);
  keepSettings();
}

function effectiveTool(): Tool {
  return toolFor({ shiftKey: shiftHeld, ctrlKey: ctrlHeld, metaKey: false });
}

function refreshTools(): void {
  const tool = effectiveTool();
  kneading.tool.value = tool;
  // No clay yet while the first stage is made ready.
  if (!clay?.isStroking) setCursorColor(tool);
}

function selectTool(tool: Tool): void {
  currentTool = tool;
  refreshTools();
  keepSettings();
}

function updateModifiers(e: KeyboardEvent): void {
  const shift = e.shiftKey, ctrl = e.ctrlKey || e.metaKey;
  if (shift === shiftHeld && ctrl === ctrlHeld) return;
  shiftHeld = shift;
  ctrlHeld = ctrl;
  refreshTools();
}

window.addEventListener("keydown", (e) => {
  updateModifiers(e);
  if (e.target instanceof HTMLSelectElement || e.target instanceof HTMLInputElement) return;
  if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "z") {
    e.preventDefault();
    undo();
    return;
  }
  if (e.ctrlKey || e.metaKey) return;
  if (e.key in TOOL_KEYS) selectTool(TOOL_KEYS[e.key]);
  else if (e.key.toLowerCase() === "m") setSymmetric(!symmetric);
  else if (e.key.toLowerCase() === "r") setWheel(!wheel);
});
window.addEventListener("keyup", updateModifiers);

/** The brush size, while its slider moves; kept with the work once let go. */
function setBrush(size: number, done: boolean): void {
  brushSize = size;
  kneading.brush.value = size;
  if (done) keepSettings();
}

mountResult($("#result"), {
  // The player takes over the slider: stop the auto-play, and come back to the finished clay.
  setMorph(t) {
    morphPlay = null;
    showTimeline(timelapse.length - 1);
    result.morph.value = t;
  },
  showFrame: showTimeline,
  setGhost(on) {
    ghost.visible = on;
    result.ghost.value = on;
  },
  look: () => lookAtPiece(0, true),
  rework: () => setJudged(false),
  deliver: () => void deliver(),
  finish: (appearance) => void deliver(appearance),
});
mountDelivery($("#delivery"), {
  share: shareSlip,
  save: saveSlip,
  keep,
  toTitle: returnToTitle,
  // The next letter, or after a piece kneaded freely the next clay; closing the letters or the
  // choice comes back to the slip, to keep the piece if not yet kept.
  next: () => freeClay ? void chooseClay(() => {}) : board.open(true),
});
mountKneading($("#kneading"), {
  selectTool,
  setSymmetric,
  setWheel,
  setBrush,
  undo,
  restart: resetClay,
  submit,
  chooseRequest: () => board.open(true),
  toTitle: pauseKneading,
});

/**
 * Makes a stage ready by `work` behind the loading screen: its meshes are made in a worker
 * (see meshes.ts), so the page goes on meanwhile. The screen is laid over at once, but its
 * paw prints show only should it take more than a moment (see overlays.module.css).
 */
async function whileLoading(work: () => Promise<void>): Promise<void> {
  overlays.loading.value = true;
  try {
    await work();
  } finally {
    overlays.loading.value = false;
  }
}

/** The title screen's workshop is shown until a request is taken on. */
let atTitle = true;

/**
 * Plays the music of where the player is: the waltz at the title screen (and what is opened from
 * it), the shuffle at the clay. It changes only as the player goes from one to the other.
 */
function tuneHere(): void {
  setTune(atTitle ? "waltz" : "shuffle");
}

/**
 * Work begins: from the title screen to the clay. The how-to comes up the first time once the
 * clay is ready (see showHelpOnce), not over the paw prints while it is made ready.
 */
function leaveTitle(): void {
  atTitle = false;
  tuneHere();
  document.body.classList.remove("at-title");
  title.shown.value = false;
  controls.enabled = true;
}

const board = mountBoard($("#board"), REQUESTS, TARGET_DEFS, {
  async accept(r) {
    if (!await putAwayWork()) return board.reopen();
    leaveTitle();
    await whileLoading(async () => {
      await loadStage(r);
      takeRequest(r);
    });
    await showWorkGuide(r.id);
  },
  showShelf() {
    board.hide();
    openShelf(() => board.reopen());
  },
});

const shelf = new Shelf(canvas, environment);

/**
 * Shows the shelf of kept pieces, to the quiet tune; `back` returns to the screen it was opened
 * from.
 */
function openShelf(back: () => void): void {
  controls.enabled = false;
  setTune("quiet");
  void shelf.open(() => {
    controls.enabled = !atTitle;
    tuneHere();
    back();
  });
}

/** Shows the ledger from the title screen, to the quiet tune as the shelf is. */
function openLedger(): void {
  setTune("quiet");
  void ledger.open(() => {
    tuneHere();
    backToTitle();
  });
}

/** Back in the workshop from the letters or the shelf, opened from the title screen. */
function backToTitle(): void {
  title.shown.value = true;
  void workshop.enter();
}

/**
 * From the clay back to the title screen, the clay left as it is: the title offers to go
 * on with it, and it is kept for a reload as always.
 */
function pauseKneading(): void {
  endStroke();
  if (judged) setJudged(false);
  showHelp(false);
  title.resume.value = leftKneading();
  atTitle = true;
  tuneHere();
  document.body.classList.add("at-title");
  controls.enabled = false;
  // Kept as left at the title: a reload comes back to it there.
  saveMeta(workMeta());
  backToTitle();
}

/** The clay being kneaded, as the title screen offers to go on with it. */
function leftKneading(): { name: string; strokes: number } {
  return { name: freeClay ? `自由にこねる（${clayName(freeClay)}）` : request.title, strokes };
}

/** Going on with the clay left kneading, once the camera is at the workbench. */
let resuming = false;

/**
 * Before new clay is taken from the title screen: true once the clay left kneading (if
 * any, and kneaded at all) may be put away, which the player is asked.
 */
async function putAwayWork(): Promise<boolean> {
  const left = title.resume.value;
  return !left || left.strokes === 0 || await asker.ask(
    `こねかけの「${left.name}」（${left.strokes}手）を片付けて、新しくこねますか？片付けた粘土は元に戻せません。`,
    "片付けてこねる",
  );
}

/** The choice of clay for free kneading; closing it, `back` goes back (to the title screen). */
async function chooseClay(back = backToTitle): Promise<void> {
  chooser.open(freeKneading(await loadRecords()).clays, async (kind) => {
    if (!await putAwayWork()) return void chooseClay(back);
    leaveTitle();
    await whileLoading(async () => {
      await loadClay(kind);
      takeFree(kind);
      showHelpOnce();
    });
  }, back);
}

/** From the delivery back to the title screen, the piece put away. */
function returnToTitle(): void {
  setJudged(false);
  atTitle = true;
  tuneHere();
  document.body.classList.add("at-title");
  controls.enabled = false;
  backToTitle();
}
const workshop = new Workshop(canvas, environment, () => titlePane.current, {
  leave: () => title.shown.value = false,
  arrive(spot) {
    if (resuming && spot === "free") {
      resuming = false;
      title.resume.value = null;
      leaveTitle();
      void showWorkGuide(freeClay ? "" : request.id);
      prepareAtmosphere();
      // Kneading again: a reload comes straight back to the clay.
      saveMeta(workMeta());
    } else if (spot === "letters") board.open(true, backToTitle);
    else if (spot === "free") void chooseClay();
    else if (spot === "shelf") openShelf(backToTitle);
    else if (spot === "ledger") openLedger();
    else if (spot === "howto") showHelp(true, backToTitle);
    else if (spot === "settings") settingsScreen.open(backToTitle);
    else credits.open(backToTitle);
  },
});
void workshop.enter();
mountTitle($("#title"), {
  go(spot) {
    resuming = false;
    workshop.go(spot);
  },
  preview: (spot) => workshop.preview(spot),
  resume() {
    resuming = true;
    workshop.go("free");
  },
});
mountOverlays($("#overlays"));
mountStart($("#start"));
const credits = mountCredits($("#credits"));
const ledger = mountLedger($("#ledger"), REQUESTS, TARGET_DEFS);
const chooser = mountClayChooser($("#clays"));
const asker = mountAsk($("#ask"));
const news = mountNews($("#news"));
const settingsScreen = mountSettings($("#settings"));

/**
 * The way back from where the player is, as the screen in front goes back by its own buttons:
 * the browser's back button takes it (see back.ts). Null at the title screen, with nowhere to
 * go back to.
 */
function wayBack(): (() => void) | null {
  // The start page is where the page begins.
  if (!start.gone.value) return null;
  // Back waits while the clay is made ready, and while the piece is finished out of sight.
  if (overlays.loading.value || (delivered && !delivery.open.value)) return () => {};
  const screen = asker.wayBack ?? news.wayBack ??
    (kneading.helpOpen.value ? () => showHelp(false) : null) ??
    shelf.wayBack ?? board.wayBack ?? chooser.wayBack ?? ledger.wayBack ??
    settingsScreen.wayBack ?? credits.wayBack;
  if (screen) return screen;
  if (atTitle) {
    // Moving in on a spot, its screen not open yet: back to the whole room.
    return title.shown.value ? null : () => {
      resuming = false;
      backToTitle();
    };
  }
  if (delivered) return returnToTitle;
  return judged ? () => setJudged(false) : pauseKneading;
}
/** Lays or lifts the back button's entry as ways back come and go (see back.ts). */
const followWayBack = followBack(wayBack);

// ---------- Stages & score ----------

interface Stage {
  target: Target;
  scorer: Scorer;
  judge: Judge;
  geometry: THREE.BufferGeometry;
  startDistance: number;
}

const stages = new Map<string, Stage>();
let stage: Stage;
/** Kneading freely instead of for a request: the clay it started from (null for a request). */
let freeClay: ClayKind | null = null;
/** Work kept while kneading freely is marked as such, with the clay (see workMeta). */
const FREE = "free:";
const CLAY_KINDS: ClayKind[] = ["blob", "donut", "pretzel", "trefoil", "quatrefoil"];

/** Symmetry mode's mirror plane: the target's, or left and right when kneading freely. */
function mirrorAxis(): number {
  return freeClay ? 0 : stage.judge.mirrorAxis;
}
let scoredVersion = -1;
let lastScoreTime = 0;

/**
 * Makes a request's stage ready (the customer's variant of the target, to knead and judge),
 * with its clay: their meshes are made in the meshing worker, the rest here, quickly.
 */
async function loadStage(r: RequestDef): Promise<Stage> {
  const plain = TARGET_DEFS.find((d) => d.id === r.target)!;
  const [meshes, start] = await Promise.all([
    targetMeshes(r.id),
    loadClay(plain.clay, plain.clayHoles),
  ]);
  let s = stages.get(r.id);
  if (!s) {
    const target = new Target({ ...plain, ...r.shape }, meshes.target);
    const scorer = new Scorer(target);
    const initial = shapeOf(start.initialMesh);
    s = {
      target,
      scorer,
      judge: new Judge(
        target,
        scorer,
        initial,
        shapeOf(meshes.reference),
        shapeOf(meshes.plainReference),
      ),
      geometry: target.createGeometry(),
      startDistance: scorer.distance(initial.positions, initial.areas, initial.centroid),
    };
    stages.set(r.id, s);
  }
  return s;
}

/** Selects a request's stage, made ready (see loadStage). */
function selectStage(r: RequestDef): void {
  endStroke();
  stage = stages.get(r.id)!;
  ghost.geometry = stage.geometry;
  previewMesh.geometry = stage.geometry;
  // The preview camera orbits the origin, so frame the farthest extent from it.
  const bounds = stage.geometry.boundingSphere!;
  const extent = bounds.radius + bounds.center.length();
  previewDistance = extent / Math.sin(THREE.MathUtils.degToRad(previewCamera.fov / 2)) * 1.05;
  placeClay(stage.target.def.clay, extent, stage.target.def.clayHoles);
  kneading.mirror.value = stage.judge.mirrorAxis === 0 ? "左右対称" : "前後対称";
}

/**
 * Puts the clay of `kind` out (its holes facing as the target asks), and the camera at the
 * front, backed off to take in the clay and `extent` (how far the target reaches) when they
 * are bigger than the blob.
 */
function placeClay(kind: ClayKind, extent: number, holes: HoleFacing = "front"): void {
  useClay(kind, holes);
  let bottom = Infinity, reach = 0;
  const p = clay.initialMesh.positions;
  for (let i = 0; i < p.length; i += 3) {
    bottom = Math.min(bottom, p[i + 1]);
    reach = Math.max(reach, Math.sqrt(p[i] ** 2 + p[i + 1] ** 2 + p[i + 2] ** 2));
  }
  // Just under the starting clay.
  shadow.position.y = bottom - 0.1;
  stageScale = Math.max(1, Math.max(reach, extent) / 1.25);
  camera.position.set(0, 1.2, CAMERA_DISTANCE);
  resize();
}

/** Starts a fresh attempt at the current target. */
function restart(): void {
  endStroke();
  clay.reset();
  strokes = 0;
  kneading.strokes.value = 0;
  undoStack = [];
  kneading.canUndo.value = false;
  timelapse.clear();
  recordStep(0);
  setJudged(false);
  scoredVersion = -1;
  updateScore(performance.now(), true);
}

/** Picks the work kept before a reload up where it was (see work.ts). */
function resume(work: Work): void {
  endStroke();
  const now = work.steps[work.steps.length - 1];
  clay.restore(now.clay);
  strokes = now.strokes;
  kneading.strokes.value = strokes;
  undoStack = work.steps.slice(0, -1).map((s) => ({ clay: s.clay, strokes: s.strokes }));
  kneading.canUndo.value = undoStack.length > 0;
  timelapse.load(work.frames);
  historyDepth = now.step;
  setSymmetric(work.symmetric);
  setWheel(!!work.wheel);
  selectTool(work.tool);
  setBrush(work.brush, false);
  setJudged(false);
  scoredVersion = -1;
  updateScore(performance.now(), true);
}

function setJudged(value: boolean): void {
  if (!value) endDelivery();
  judged = value;
  document.body.classList.toggle("judged", value);
  result.open.value = value;
  result.ghost.value = ghost.visible = false;
  morph = null;
  morphPlay = null;
  wobbleStart = null;
  result.morph.value = 0;
  showMorph(0);
  result.frames.value = timelapse.length;
  showTimeline(timelapse.length - 1);
}

/**
 * Shows frame k of the timelapse; the last frame is the clay itself. Scrubbing back takes
 * over from the morph.
 */
function showTimeline(k: number): void {
  const last = timelapse.length - 1;
  result.frame.value = k;
  result.frameStrokes.value = timelapse.strokesAt(k);
  timelapseMesh.visible = k < last;
  if (k < last) {
    timelapseMesh.geometry = timelapse.geometryAt(k);
    morphPlay = null;
    result.morph.value = 0;
    showMorph(0);
  }
  clay.mesh.visible = k === last && morphShown === 0;
}

/** Shows the morph at t (0 = the clay itself). Built on first use after submitting. */
function showMorph(t: number): void {
  morphShown = t;
  clay.mesh.visible = t === 0 && !timelapseMesh.visible;
  morphMesh.visible = t > 0;
  if (t === 0) return;
  morph ??= new Morph(clay.rest, clay.index, stage.target, CLAY_VOLUME);
  const frame = morph.at(t);
  const geometry = new THREE.BufferGeometry();
  geometry.setIndex(new THREE.BufferAttribute(frame.index, 1));
  geometry.setAttribute("position", new THREE.BufferAttribute(frame.positions, 3));
  geometry.setAttribute("normal", new THREE.BufferAttribute(frame.normals, 3));
  morphMesh.geometry.dispose();
  morphMesh.geometry = geometry;
  morphMaterial.color.lerpColors(clayMaterial.color, TARGET_COLOR, t);
  // Lit as the clay is at first, and as the target is once it is there.
  morphMaterial.envMapIntensity = THREE.MathUtils.lerp(
    clayMaterial.envMapIntensity,
    scene.environmentIntensity,
    t,
  );
}

function submit(): void {
  endStroke();
  stopSpin();
  if (freeClay) {
    // Nothing to judge: on to how to finish it, and how it comes across meanwhile.
    judgement = null;
    setJudged(true);
    sounds.jingle(null);
    lookAtPiece();
    return;
  }
  const shape = shapeOf({ positions: clay.rest, index: clay.index });
  judgement = stage.judge.judge(shape, strokes, request.wish);
  result.judgement.value = judgement;
  result.requestId.value = request.id;
  result.strokes.value = strokes;
  setJudged(true);
  sounds.jingle(judgement.rank);
  // Looked at once the morph has played: the model would stall it, as it shares the GPU.
  lookAtPiece(Object.values(MORPH_PLAY).reduce((a, b) => a + b));
  // Build the morph once the result has painted, during the auto-play's wait.
  setTimeout(() => {
    if (judged) morph ??= new Morph(clay.rest, clay.index, stage.target, CLAY_VOLUME);
  }, 50);
  morphPlay = { start: performance.now(), phase: "wait" };
}

/** Asks of the latest look at the piece: an answer to an older one is let go. */
let lookAsk = 0;
/** What the latest look at the piece's air saw, once it has: the slip's word follows it. */
let atmosphereSeen: Promise<AtmosphereRating | null> = Promise.resolve(null);
/** The same for the labels of a piece kneaded freely. */
let tagsSeen: Promise<Tags | null> = Promise.resolve(null);

/**
 * Has the VLM look at the piece judged now: the air its letter hopes for (see atmosphere.ts),
 * after `delay` ms, or how a piece kneaded freely comes across (see tags.ts). `trying`: the
 * player asked for this look with the setting off, which is turned on only once it has seen.
 */
function lookAtPiece(delay = 0, trying = false): void {
  if (freeClay) tagsSeen = lookAtTags(trying);
  else atmosphereSeen = lookAtAtmosphere(delay, trying);
}

/**
 * Looks at the piece by `rate` if the player lets it be looked at (or is `trying` it), `delay`
 * ms from now: `show` follows along as the model comes and looks. Null when nothing was seen
 * (or not of the piece judged now).
 */
async function look<T>(
  show: (view: Looking<T>) => void,
  rate: (loading: (share: number) => void) => Promise<T>,
  delay: number,
  trying: boolean,
): Promise<T | null> {
  const asked = ++lookAsk;
  if (!settings.atmosphere.value && !trying) {
    show({ state: "off" });
    return null;
  }
  const current = () => asked === lookAsk && judged;
  // While the model loads, it says so from the start, through the wait too.
  const loading = (share: number) => {
    if (current()) show(share < 1 ? { state: "loading", share } : { state: "looking" });
  };
  loading(followLoading(loading));
  if (!await canLook()) {
    if (current()) show({ state: "unsupported" });
    return null;
  }
  if (!current()) return null;
  if (delay > 0) {
    await wait(delay);
    if (!current()) return null;
  }
  try {
    const seen = await rate(loading);
    // Kept on only once the model has seen: a look that fails, or a page that crashes or is
    // reloaded meanwhile, leaves it off for the next visit.
    if (trying && !settings.atmosphere.value) {
      settings.atmosphere.value = true;
      saveSettings();
    }
    if (!current()) return null;
    show({ state: "rated", seen });
    return seen;
  } catch (e) {
    console.error(e);
    if (current()) show({ state: "failed" });
    return null;
  }
}

/** Looks at the air the letter hopes for, if there is one (see atmosphere.ts), after `delay` ms. */
function lookAtAtmosphere(delay: number, trying: boolean): Promise<AtmosphereRating | null> {
  const { atmosphere, appearance } = request;
  if (!atmosphere) {
    ++lookAsk;
    result.atmosphere.value = null;
    return Promise.resolve(null);
  }
  // The photos are taken as it is looked at (the untouched clay and the target's, only the
  // first time).
  const { initialMesh } = clay, { geometry } = stage;
  const startClay = () => {
    const g = new THREE.BufferGeometry();
    g.setIndex(new THREE.BufferAttribute(initialMesh.index, 1));
    g.setAttribute("position", new THREE.BufferAttribute(initialMesh.positions, 3));
    g.computeVertexNormals();
    g.computeBoundingSphere();
    const photos = atmospherePhotos(appearance, g);
    g.dispose();
    return photos;
  };
  const { label } = atmosphere;
  return look(
    (view) => result.atmosphere.value = { label, ...view },
    (loading) =>
      rateAtmosphere(
        request.id,
        atmosphere,
        atmospherePhotos(appearance),
        startClay,
        () => atmospherePhotos(appearance, geometry),
        loading,
      ),
    delay,
    trying,
  );
}

/**
 * Looks at how the piece kneaded freely comes across (see tags.ts), its shape measured on the
 * clay. Its material is chosen only after, so it is shown in the usual ceramic finish.
 */
function lookAtTags(trying: boolean): Promise<Tags | null> {
  return look(
    (view) => result.tags.value = view,
    (loading) =>
      rateTags(
        atmospherePhotos(DEFAULT_APPEARANCES.ceramic),
        shapeOf({ positions: clay.rest, index: clay.index }),
        loading,
      ),
    0,
    trying,
  );
}

/** Side of the square photo the VLM looks at, in pixels. */
const ATMOSPHERE_PHOTO = 448;

/**
 * What the VLM looks at: `geometry` (the clay as it stands, unless given) with the requested
 * material, color, and finish, from each photo view, on plain paper.
 */
function atmospherePhotos(appearance: Appearance, geometry?: THREE.BufferGeometry): ImageData[] {
  // As the clay lies, as its anchors were taken, even if the piece already stands on show.
  const material = clay.mesh.material, shown = clay.mesh.geometry;
  const turned = clay.mesh.quaternion.clone();
  clay.mesh.material = appearanceMaterial(appearance);
  if (geometry) clay.mesh.geometry = geometry;
  clay.mesh.quaternion.identity();
  const photos = takePhotos();
  clay.mesh.material = material;
  clay.mesh.geometry = shown;
  clay.mesh.quaternion.copy(turned);
  return photos.map((photo) => {
    const c = document.createElement("canvas");
    c.width = c.height = ATMOSPHERE_PHOTO;
    const g = c.getContext("2d")!;
    g.fillStyle = "#f6f1ea";
    g.fillRect(0, 0, ATMOSPHERE_PHOTO, ATMOSPHERE_PHOTO);
    g.drawImage(photo, 0, 0, ATMOSPHERE_PHOTO, ATMOSPHERE_PHOTO);
    return g.getImageData(0, 0, ATMOSPHERE_PHOTO, ATMOSPHERE_PHOTO);
  });
}

/**
 * Renders the finished clay alone from each photo view into the corner of the canvas and
 * copies it out right away (before the canvas is shown), with a transparent background.
 */
function takePhotos(): HTMLCanvasElement[] {
  const size = Math.min(canvas.clientWidth, canvas.clientHeight);
  const pixels = Math.round(size * renderer.getPixelRatio());
  const others = [ghost, morphMesh, timelapseMesh, turntable, wheelGuide, ...cursors]
    .filter((o) => o.visible);
  const clayVisible = clay.mesh.visible, clayScale = clay.mesh.scale.clone();
  for (const o of others) o.visible = false;
  clay.mesh.visible = true;
  clay.mesh.scale.setScalar(pieceScale);
  clay.mesh.updateMatrixWorld();
  keyLight.visible = false;
  scene.add(photoCamera);
  const sphere = clay.mesh.geometry.boundingSphere!.clone().applyMatrix4(clay.mesh.matrixWorld);
  const distance = sphere.radius / Math.sin(THREE.MathUtils.degToRad(photoCamera.fov / 2)) * 1.08;
  const photos = PHOTO_VIEWS.map((view) => {
    photoCamera.position.copy(sphere.center).addScaledVector(view, distance);
    photoCamera.lookAt(sphere.center);
    renderer.setScissorTest(true);
    renderer.setScissor(0, 0, size, size);
    renderer.setViewport(0, 0, size, size);
    renderer.setClearColor(0x000000, 0);
    renderer.render(scene, photoCamera);
    const photo = document.createElement("canvas");
    photo.width = photo.height = pixels;
    // The viewport sits at the bottom left of the drawing buffer.
    photo.getContext("2d")!.drawImage(
      canvas,
      0,
      canvas.height - pixels,
      pixels,
      pixels,
      0,
      0,
      pixels,
      pixels,
    );
    return photo;
  });
  scene.remove(photoCamera);
  keyLight.visible = true;
  for (const o of others) o.visible = true;
  clay.mesh.visible = clayVisible;
  clay.mesh.scale.copy(clayScale);
  return photos;
}

const wait = (ms: number) => new Promise((done) => setTimeout(done, ms));
const pngOf = (c: HTMLCanvasElement) =>
  new Promise<Blob>((done) => c.toBlob((b) => done(b!), "image/png"));

/**
 * Delivers the piece using the selected material's process and its own fixed color and finish.
 */
async function deliver(freeAppearance?: Appearance): Promise<void> {
  if (delivered || (!judgement && !freeClay)) return;
  delivered = true;
  result.open.value = false;
  controls.enabled = false;
  // Delivered, the work is done: a reload goes to the title screen. The ledger notes it (a
  // piece kneaded freely is for no one, so it is not in the ledger). The lesson is over too,
  // its marks off the piece before it is photographed.
  clearWork();
  skipTutorial();
  delivery.progress.value = "";
  /** What the delivery opened, told once the slip is stamped. */
  let opened: Opened = { stages: [], finishes: [] };
  if (judgement && !freeClay) {
    const before = await loadRecords();
    const saved = await recordDelivery(request.id, judgement, strokes);
    const after = await loadRecords();
    const arrived = REQUESTS.filter((r) =>
      !accessTo(r.id, before).available && accessTo(r.id, after).available
    );
    const challenge = challengeFor(request.id);
    const record = after.get(request.id);
    const messages: string[] = [];
    if (challenge) {
      messages.push(
        judgement.total >= 70
          ? "お題達成！"
          : "納品しました。お題達成はB以上（70点）。またいつでも挑戦できます。",
      );
      if (!mastered(request.id, before.get(request.id)) && mastered(request.id, record)) {
        messages.push("腕試しのスタンプを獲得！");
      }
    }
    if (challenge && cleared(record) || arrived.length) messages.push(progressSummary(after));
    if (!saved) {
      messages.push(
        "進み具合を保存できませんでした。このタブでは続けられますが、再読み込みやタブを閉じると失われます。",
      );
    }
    delivery.progress.value = messages.join(" ");
    opened = openedBy(before, after);
  }
  delivery.kept.value = "not yet";
  document.body.classList.add("delivering");
  result.open.value = false;
  result.ghost.value = ghost.visible = false;
  morphPlay = null;
  result.morph.value = 0;
  showMorph(0);
  showTimeline(timelapse.length - 1);
  controls.enabled = false;

  deliveredAppearance = freeClay
    ? freeAppearance ?? DEFAULT_APPEARANCES.ceramic
    : request.appearance;
  const appearance = deliveredAppearance;
  const kiln = requiresKiln(appearance), glows = glowsInKiln(appearance);
  const putOut = kiln ? sounds.kiln() : null;
  if (!kiln) sounds.iris(true);
  await (kiln ? raiseKiln() : closeIris(appearance.material));
  // Set the piece's own appearance while it is out of sight. Keep non-glowing finishes on the
  // original material instance; only fired soil and pottery need the temporary kiln glow.
  const finishedMaterial = appearanceMaterial(appearance);
  if (glows) {
    deliveryMaterial.copy(finishedMaterial);
    deliveryMaterial.needsUpdate = true;
    deliveryMaterial.emissive.set(0xff6a2b);
    deliveryMaterial.emissiveIntensity = 0.7;
    clay.mesh.material = deliveryMaterial;
  } else {
    clay.mesh.material = finishedMaterial;
  }
  pieceScale = shrinksInKiln(appearance) ? FIRED_SCALE : 1;
  standUp();
  const date = new Date();
  // The piece may still be being looked at: the slip's word waits for it as long as the piece
  // is out of sight, and no longer.
  const hidden = wait(kiln ? DELIVERY.fire : DELIVERY.set);
  const seen = <T>(seeing: Promise<T | null>) => Promise.race([seeing, hidden.then(() => null)]);
  const air = judgement ? await seen(atmosphereSeen) : null;
  const data = judgement
    ? slipOf(judgement, stage.target.def, request, strokes, date, air)
    : freeSlipOf(strokes, appearanceLabel(appearance), date, await seen(tagsSeen));
  deliveryMaterial.emissiveIntensity = 0;
  const photos = takePhotos();
  deliveryMaterial.emissiveIntensity = glows ? 0.7 : 0;
  const face = await faceImage(data.face, data.mood);
  const [blank, photo] = await Promise.all([
    pngOf(drawSlip(data, photos, face, false)),
    // The photo breaks the score down beside the slip; a free piece has none.
    pngOf(
      judgement
        ? drawPhoto(
          drawSlip(data, photos, face),
          judgement,
          air && request.atmosphere ? { label: request.atmosphere.label, rating: air } : null,
        )
        : drawSlip(data, photos, face),
    ),
  ]);
  const day = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}${
    String(date.getDate()).padStart(2, "0")
  }`;
  slipFile = new File([photo], `ねこ商会_納品票_${data.item}_${day}.png`, { type: "image/png" });
  delivery.canShare.value = !!navigator.canShare?.({ files: [slipFile] });
  if (delivery.slip.value) URL.revokeObjectURL(delivery.slip.value);
  delivery.slip.value = URL.createObjectURL(blank);
  await hidden;
  putOut?.();
  if (!delivered) return;

  // Out of sight no more, the piece turns slowly on show, as on the title screen.
  controls.autoRotate = !settings.still.value;
  if (kiln) {
    if (glows) firedAt = performance.now();
    sounds.bell();
    await lowerKiln();
  } else {
    sounds.iris(false);
    await openIris();
  }
  await wait(500);
  if (!delivered) return;

  asideAt = performance.now();
  // The slip slides in as the piece moves aside, and then the shop stamps it.
  delivery.open.value = true;
  sounds.slip();
  await wait(SLIP_IN.delay + SLIP_IN.duration + DELIVERY.rest);
  if (!delivered) return;
  delivery.stamped.value = true;
  // As the stamp comes down (see ui/delivery.tsx), and the slip shines a moment after it.
  sounds.stamp(0.14);
  sounds.kirarin(0.85);
  if (!opened.stages.length && !opened.finishes.length) return;
  await wait(NEWS_AFTER);
  if (delivered) await news.tell(opened);
}

/** How long after the stamp the kitten tells what the delivery opened (ms). */
const NEWS_AFTER = 1400;

/**
 * What a delivery opened, from the ledger before and after it: stages (a challenge cleared,
 * new letters, new clays to knead freely) and finishes (new materials to finish them in).
 */
function openedBy(before: Records, after: Records): Opened {
  const stages: string[] = [];
  for (const c of CHALLENGES) {
    if (!cleared(before.get(c.request)) && cleared(after.get(c.request))) {
      stages.push(`お題「${c.title}」を達成したよ！`);
    }
  }
  const arrived = REQUESTS.filter((r) =>
    !accessTo(r.id, before).available && accessTo(r.id, after).available
  );
  for (const r of arrived) {
    const challenge = challengeFor(r.id);
    if (challenge) {
      stages.push(`新しいお題「${challenge.title}」が届いたよ！「${r.title}」をこねてみよう。`);
    }
  }
  const letters = arrived.filter((r) => !challengeFor(r.id));
  if (letters.length) {
    stages.push(`新しい手紙が届いたよ！${letters.map((r) => `「${r.title}」`).join("")}`);
  }
  const was = freeKneading(before), now = freeKneading(after);
  const clays = [...now.clays].filter((k) => !was.clays.has(k));
  if (clays.length) {
    stages.push(
      `自由にこねるで${
        clays.map((k) => `「${clayName(k)}」`).join("")
      }の粘土が使えるようになったよ！`,
    );
  }
  const finishes = [...now.materials].filter((m) => !was.materials.has(m)).map((m) =>
    `自由にこねるで「${MATERIAL_NAMES[m]}」の仕上げができるようになったよ！`
  );
  return { stages, finishes };
}

const _upright = new THREE.Quaternion();
const _bottom = new THREE.Vector3();

/**
 * Stands the piece on show up as its letter's thumbnail shows it, if that looks at it from
 * elsewhere than the front (a face laid face up, say): turned so that view comes to the
 * front, its shadow lowered to its new bottom.
 */
function standUp(): void {
  const view = judgement && stage.target.def.thumbView;
  if (!view) return;
  clay.mesh.quaternion.setFromUnitVectors(_bottom.set(...view).normalize(), THUMB_VIEW);
  const p = clay.mesh.geometry.getAttribute("position");
  let bottom = Infinity;
  for (let i = 0; i < p.count; i++) {
    bottom = Math.min(
      bottom,
      _bottom.fromBufferAttribute(p, i).applyQuaternion(clay.mesh.quaternion).y,
    );
  }
  shadowRest = shadow.position.y;
  shadow.position.y = bottom * pieceScale - 0.1;
}

/** A copy of clay positions, turned as the piece on show stands (see standUp), for the shelf. */
function asStanding(positions: Float32Array): Float32Array {
  const copy = new THREE.BufferAttribute(positions.slice(), 3);
  copy.applyMatrix4(new THREE.Matrix4().makeRotationFromQuaternion(clay.mesh.quaternion));
  return copy.array as Float32Array;
}

/** Back from the delivery screen (or never there): the raw clay, centred. */
function endDelivery(): void {
  if (delivered) controls.autoRotate = false;
  delivered = false;
  document.body.classList.remove("delivering");
  overlays.kiln.value = overlays.iris.value = false;
  delivery.open.value = false;
  delivery.stamped.value = false;
  clay.mesh.material = clayMaterial;
  pieceScale = 1;
  if (!clay.mesh.quaternion.equals(_upright.identity())) {
    clay.mesh.quaternion.identity();
    shadow.position.y = shadowRest;
  }
  firedAt = asideAt = null;
  controls.enabled = true;
}

/** Puts the delivered piece on the shelf, with its photo and a copy of the letter. */
async function keep(): Promise<void> {
  if (!slipFile || (!judgement && !freeClay)) return;
  delivery.kept.value = "keeping";
  // A piece kneaded freely has no letter to copy (there may have been no request at all).
  const kept = judgement
    ? {
      request: {
        title: request.title,
        client: request.client,
        letter: request.letter,
        wish: request.wish.label,
        item: request.item,
        exhibit: request.exhibit,
      },
      rank: judgement.rank,
      total: judgement.total,
    }
    : {
      request: {
        title: "自由作品",
        client: "",
        letter: "",
        wish: "",
        item: "自由作品",
        exhibit: "一点もの",
      },
      rank: "",
      total: 0,
      free: true,
    };
  try {
    await keepPiece({
      date: Date.now(),
      ...kept,
      positions: asStanding(clay.rest),
      index: clay.index.slice(),
      photo: slipFile,
      // Every frame stood up as the piece, so that scrubbing through them turns nothing.
      timelapse: timelapse.snapshot().map((f) => ({ ...f, positions: asStanding(f.positions) })),
      appearance: deliveredAppearance,
    });
    delivery.kept.value = "kept";
    sounds.keep();
  } catch {
    // No storage (a private window, say).
    delivery.kept.value = "failed";
  }
}

function saveSlip(): void {
  if (!slipFile) return;
  sounds.shutter();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(slipFile);
  a.download = slipFile.name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

async function shareSlip(): Promise<void> {
  if (!slipFile) return;
  try {
    await navigator.share({ files: [slipFile], title: "ねこ商会 納品票" });
  } catch {
    // Cancelled by the player.
  }
}

/** The customer's letter being worked on (the last one, when kneading freely). */
let request: RequestDef;
/** The material, fixed color, and finish used for the piece being delivered. */
let deliveredAppearance: Appearance = DEFAULT_APPEARANCES.ceramic;

/** Takes on a customer's request: its target, from fresh clay or from the work kept. */
function takeRequest(r: RequestDef, work?: Work): void {
  title.resume.value = null;
  freeClay = null;
  kneading.free.value = result.free.value = false;
  request = r;
  kneading.request.value = { id: r.id, title: r.title, wish: r.wish.label };
  selectStage(r);
  if (work) resume(work);
  else restart();
  prepareAtmosphere();
}

/** Offers the request's lesson on each entry, or introduces the general controls. */
async function showWorkGuide(requestId: string): Promise<void> {
  skipTutorial();
  if (!hasTutorial(requestId)) {
    showHelpOnce();
    return;
  }
  markHelpSeen();
  if (
    await asker.ask(
      "操作説明を受けますか？道具を使いながら、こね方を順番に練習できます。",
      "受ける",
      "受けずにこねる",
    )
  ) {
    startTutorial(requestId);
  }
}

/**
 * Starts loading the VLM behind the kneading, if the request asks for an air (or the clay is
 * kneaded freely) and the player lets it look: it is ready, or nearer, by the time the piece
 * is submitted.
 */
function prepareAtmosphere(): void {
  if (!atTitle && (freeClay || request.atmosphere) && settings.atmosphere.value) {
    void prepareToLook();
  }
}

/** Kneads freely from a clay of `kind`: no request, no target, nothing judged. */
function takeFree(kind: ClayKind, work?: Work): void {
  title.resume.value = null;
  startTutorial("");
  endStroke();
  freeClay = kind;
  kneading.free.value = result.free.value = true;
  void loadRecords().then((r) => result.materials.value = freeKneading(r).materials);
  kneading.mood.value = "自由にこねよう";
  kneading.mirror.value = "左右対称";
  placeClay(kind, 0);
  if (work) resume(work);
  else restart();
  prepareAtmosphere();
}

function updateScore(now: number, force: boolean): void {
  // Kneading freely, there is nothing to be close to.
  if (freeClay || clay.version === scoredVersion) return;
  if (!force && now - lastScoreTime < SCORE_INTERVAL_MS) return;
  const d = stage.scorer.distance(clay.rest, clay.restAreas, clay.restStats.centroid);
  const near = closeness(d, stage.startDistance);
  kneading.mood.value = MOODS.find(([min]) => near >= min)![1];
  scoredVersion = clay.version;
  lastScoreTime = now;
}

// ---------- Frame loop ----------

const RING_AXIS = new THREE.Vector3(0, 0, 1);

function placeCursors(hits: Hit[]): void {
  cursors.forEach((ring, k) => {
    const hit = hits[k];
    ring.visible = hit !== undefined;
    if (!hit) return;
    ring.position.copy(hit.point).addScaledVector(hit.normal, 0.01);
    ring.quaternion.setFromUnitVectors(RING_AXIS, hit.normal);
    ring.scale.setScalar(clay.isStroking ? clay.brushRadius : brushRadiusAt(hits[0].point));
  });
  const hit = hits[0];
  wheelGuide.visible = wheel && hit !== undefined;
  if (!wheelGuide.visible) return;
  const around = Math.max(0.01, Math.hypot(hit.point.x, hit.point.z));
  wheelGuide.position.set(0, hit.point.y, 0);
  wheelGuide.scale.set(around, 1, around);
  wheelGuide.material.color.copy(cursorMaterial.color);
}

/**
 * Marks where the lesson's step works (see TutorialMark): a ring as big as the brush the step
 * takes, and an arrow on out to where the clay should reach while it falls short. None once
 * the piece is judged.
 */
function placeLessonMarks(): void {
  lessonMarks.traverse((part) => {
    if (part instanceof THREE.ArrowHelper) part.dispose();
  });
  lessonMarks.clear();
  const step = tutorialGuide();
  if (!step?.marks || result.open.value) return;
  const { scale, shift } = stage.target;
  const placed = (p: [number, number, number]) =>
    new THREE.Vector3(...p).multiplyScalar(scale).add(shift);
  // Where the clay's surface lies toward `p` from the middle.
  const surfaceToward = (p: THREE.Vector3) => {
    const inward = p.clone().normalize().negate();
    return clay.raycast(new THREE.Ray(p.clone().addScaledVector(inward, -10), inward));
  };
  for (const mark of step.marks) {
    const hit = surfaceToward(placed(mark.from));
    if (!hit) continue;
    // A mark's ring and arrow, shown while the clay where it lies faces the camera.
    const shown = new THREE.Group();
    shown.userData = { point: hit.point, normal: hit.normal };
    lessonMarks.add(shown);
    const ring = new THREE.Mesh(lessonRing, lessonMaterial);
    ring.position.copy(hit.point).addScaledVector(hit.normal, 0.01);
    ring.quaternion.setFromUnitVectors(RING_AXIS, hit.normal);
    ring.scale.setScalar((step.brush?.max ?? step.brush?.min ?? 0.15) * scale);
    ring.renderOrder = 2;
    shown.add(ring);
    if (!mark.to) continue;
    const to = placed(mark.to);
    const reached = surfaceToward(to)?.point.length() ?? 0;
    if (to.length() - reached < 0.15) continue;
    const reach = to.distanceTo(hit.point);
    const arrow = new THREE.ArrowHelper(
      to.clone().sub(hit.point).normalize(),
      hit.point,
      reach,
      LESSON_COLOR,
      Math.min(0.2, reach / 2),
      0.14,
    );
    for (const part of [arrow.line, arrow.cone]) {
      (part.material as THREE.Material).depthTest = false;
      part.renderOrder = 2;
    }
    shown.add(arrow);
  }
}

/**
 * Hides the lesson's marks on the far side of the clay, which would show through it: those
 * where it faces away from the camera, a little past edge on.
 */
function faceLessonMarks(): void {
  for (const shown of lessonMarks.children) {
    const { point, normal } = shown.userData as { point: THREE.Vector3; normal: THREE.Vector3 };
    shown.visible = normal.dot(_toPoint.subVectors(camera.position, point).normalize()) > -0.15;
  }
}

/** Turns the wheel under the clay, its top just under the clay's lowest point. */
function turnWheel(dt: number): void {
  if (wheel && !judged && !delivered) spin = (spin + TURNTABLE_SPEED * dt) % (2 * Math.PI);
  else if (spin > 0) {
    // Switched off, the wheel runs on round to the front and settles there.
    const left = 2 * Math.PI - spin;
    spin += Math.min(TURNTABLE_SPEED * dt, left * (1 - Math.exp(-6 * dt)));
    if (2 * Math.PI - spin < 1e-3) spin = 0;
  }
  clay.mesh.rotation.y = turntable.rotation.y = spin;
  turntable.visible = (wheel || spin > 0) && !delivered;
  if (!turntable.visible) return;
  const p = clay.mesh.geometry.getAttribute("position").array;
  let bottom = Infinity;
  for (let i = 1; i < p.length; i += 3) bottom = Math.min(bottom, p[i]);
  turntable.position.y = bottom * pieceScale - 0.06;
  // Seen from below, the disc fades out of the way of the clay's underside: by how far the
  // camera is under its top, from nearly edge on to a little below.
  const under = (turntable.position.y + 0.06 - camera.position.y) /
    camera.position.distanceTo(turntable.position);
  const opacity = 1 - 0.8 * THREE.MathUtils.smoothstep(under, -0.05, 0.15);
  for (const m of turntable.material as THREE.Material[]) {
    m.opacity = opacity;
    m.depthWrite = opacity === 1;
  }
}

/**
 * Tints the clay where the brush works: during a stroke, as it is working; otherwise where
 * it would work if pressed at `hover` (the pointer over the clay, or looking through a
 * hole), with the brush as big as it would be there.
 */
function tintBrush(hover: Hit | Hole | null): void {
  if (hover) clay.brushRadius = brushRadiusAt(hover.point);
  const viewDir = camera.getWorldDirection(_viewDir);
  clay.showBrush(strokeTool ?? effectiveTool(), hover, viewDir, strokeRepeat(), tintColor);
}

function hoverCursors(): Hit[] {
  const hit = hovering && !judged ? castPointer() : null;
  if (!hit) return [];
  return symmetric ? [hit, mirrorHit(hit, mirrorAxis())] : [hit];
}

function applyWobble(now: number): void {
  let s = 1;
  if (wobbleStart !== null) {
    const age = (now - wobbleStart) / 1000;
    if (age > 1.2) wobbleStart = null;
    else s = 1 + 0.08 * Math.exp(-4 * age) * Math.sin(16 * age);
  }
  // Squash and stretch about the centre, keeping the volume.
  const side = pieceScale / Math.sqrt(s);
  clay.mesh.scale.set(side, s * pieceScale, side);
  morphMesh.scale.set(side, s, side);
  timelapseMesh.scale.set(side, s, side);
}

function resize(): void {
  // Never smaller than the letters' thumbnails, drawn into the corner of the drawing buffer:
  // a page laid out at no size at all (in a hidden frame, say) would leave one of none, which
  // cannot be drawn from.
  const w = Math.max(window.innerWidth, THUMB_SIZE), h = Math.max(window.innerHeight, THUMB_SIZE);
  renderer.setSize(w, h, false);
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
  // Portrait screens: back off so the clay still fits horizontally.
  camera.position.setLength(CAMERA_DISTANCE * stageScale * Math.max(1, 0.75 / camera.aspect));
}

/** Moves the piece aside for the delivery slip: left on wide screens, up on tall ones. */
function setAside(now: number): void {
  const w = canvas.clientWidth, h = canvas.clientHeight;
  const t = asideAt === null ? 0 : easeInOut(Math.min(1, (now - asideAt) / DELIVERY.aside));
  if (t === 0) camera.clearViewOffset();
  else if (w >= h) camera.setViewOffset(w, h, 0.24 * w * t, 0, w, h);
  else camera.setViewOffset(w, h, 0, 0.24 * h * t, w, h);
}

function render(): void {
  const w = canvas.clientWidth, h = canvas.clientHeight;
  renderer.setScissorTest(false);
  renderer.setViewport(0, 0, w, h);
  renderer.setClearColor(0x000000, 0);
  renderer.render(scene, camera);

  // Target preview, drawn into the box of #target-view with the same view angle (hidden
  // while delivering).
  if (delivered || freeClay) return;
  const rect = targetView.current!.getBoundingClientRect();
  const x = rect.left, y = h - rect.bottom;
  renderer.setScissorTest(true);
  renderer.setScissor(x, y, rect.width, rect.height);
  renderer.setViewport(x, y, rect.width, rect.height);
  renderer.setClearColor(0xfffaf3, 0.9);
  previewCamera.aspect = rect.width / rect.height;
  previewCamera.updateProjectionMatrix();
  previewCamera.position.copy(camera.position).setLength(previewDistance);
  previewCamera.quaternion.copy(camera.quaternion);
  renderer.render(previewScene, previewCamera);
}

// ---------- The letters' thumbnails ----------

/** Grid spacing of the thumbnails' meshes: coarse, as they are drawn small. */
const THUMB_CELL = 0.08;
/** A thumbnail's size on screen (CSS pixels); drawn at the screen's own resolution. */
const THUMB_SIZE = 96;
/** Seen as the kneading starts off: from the front, a little above (see TargetDef.thumbView). */
const THUMB_VIEW = new THREE.Vector3(0, 1.2, CAMERA_DISTANCE).normalize();
/** Requests whose letter still waits for its thumbnail: one is drawn a frame. */
const thumbsToDraw = [...REQUESTS];

/**
 * The request's target as the preview shows it, drawn alone into the corner of the canvas
 * and copied out right away (as takePhotos does), the corner cleared after.
 */
function drawThumb(r: RequestDef): string {
  const def = { ...TARGET_DEFS.find((d) => d.id === r.target)!, ...r.shape };
  const { positions, index } = meshSdf(def.sdf, def.bound, THUMB_CELL);
  const geometry = new THREE.BufferGeometry();
  geometry.setIndex(new THREE.BufferAttribute(index, 1));
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  const shown = previewMesh.geometry;
  previewMesh.geometry = geometry;
  const { center, radius } = geometry.boundingSphere!;
  const distance = radius / Math.sin(THREE.MathUtils.degToRad(previewCamera.fov / 2)) * 1.05;
  previewCamera.aspect = 1;
  previewCamera.updateProjectionMatrix();
  const view = def.thumbView ? new THREE.Vector3(...def.thumbView).normalize() : THUMB_VIEW;
  previewCamera.position.copy(center).addScaledVector(view, distance);
  previewCamera.lookAt(center);
  renderer.setScissorTest(true);
  renderer.setScissor(0, 0, THUMB_SIZE, THUMB_SIZE);
  renderer.setViewport(0, 0, THUMB_SIZE, THUMB_SIZE);
  renderer.setClearColor(0x000000, 0);
  renderer.render(previewScene, previewCamera);
  const pixels = Math.round(THUMB_SIZE * renderer.getPixelRatio());
  const thumb = document.createElement("canvas");
  thumb.width = thumb.height = pixels;
  // The viewport sits at the bottom left of the drawing buffer.
  thumb.getContext("2d")!.drawImage(
    canvas,
    0,
    canvas.height - pixels,
    pixels,
    pixels,
    0,
    0,
    pixels,
    pixels,
  );
  renderer.clear();
  renderer.setScissorTest(false);
  previewMesh.geometry = shown;
  geometry.dispose();
  return thumb.toDataURL();
}

let last = performance.now();
function frame(now: number): void {
  const dt = Math.min(0.05, (now - last) / 1000);
  last = now;
  followWayBack();
  // Before this frame's view is drawn over the canvas.
  const letter = thumbsToDraw.shift();
  if (letter) letterThumbs.value = new Map(letterThumbs.value).set(letter.id, drawThumb(letter));
  if (shelf.isOpen || atTitle) {
    const view = shelf.isOpen ? shelf : workshop;
    view.update(dt);
    view.render(renderer);
    requestAnimationFrame(frame);
    return;
  }
  controls.update(dt);

  if (clay.isStroking) {
    stepStroke(dt);
    placeCursors(clay.strokeCursors);
    tintBrush(null);
  } else {
    const hover = hoverCursors();
    placeCursors(hover);
    tintBrush(hover[0] ?? (hovering && !judged ? holeAtPointer() : null));
  }

  faceLessonMarks();
  clay.update(dt);
  turnWheel(dt);
  updateScore(now, false);
  if (morphPlay) {
    const { t, phase } = morphPlayAt(now - morphPlay.start);
    if (phase !== morphPlay.phase && (phase === "hold" || phase === "done")) {
      wobbleStart = now;
      sounds.wobble();
    }
    morphPlay.phase = phase;
    result.morph.value = t;
    if (phase === "done") morphPlay = null;
  }
  // Re-mesh the morph at most once a frame, however fast the slider moves.
  const t = result.morph.value;
  if (judged && t !== morphShown) showMorph(t);
  applyWobble(now);
  // Fresh from the kiln, the piece glows and cools.
  if (firedAt !== null) {
    deliveryMaterial.emissiveIntensity = 0.7 * Math.max(0, 1 - (now - firedAt) / DELIVERY.cool);
  }
  setAside(now);
  render();
  requestAnimationFrame(frame);
}

window.addEventListener("resize", resize);
// The lesson's marks follow its steps and the clay as it is kneaded.
effect(() => {
  void tutorial.value;
  void kneading.strokes.value;
  placeLessonMarks();
});
// The settings apply as they change: how finely the scene is drawn, and whether it keeps still.
effect(() => {
  renderer.setPixelRatio(
    settings.quality.value === "light" ? 1 : Math.min(window.devicePixelRatio, 2),
  );
  resize();
});
effect(() => {
  workshop.still = shelf.still = settings.still.value;
});
effect(() => setVolumes(settings.sound.value, settings.music.value));
// The clay being kneaded (and on the result screen), not the finished piece.
effect(() => {
  const colors = CLAY_COLORS[settings.clay.value];
  clayMaterial.color.setHex(colors.color);
  clayMaterial.envMapIntensity = scene.environmentIntensity * colors.environment;
  for (const m of [clayMaterial, morphMaterial]) {
    // A material's own share of the room's light holds only with the room as its own map.
    m.envMap = environment;
    m.sheen = colors.sheen;
    m.sheenColor.setHex(colors.sheenColor);
  }
  tintColor.setHex(colors.tint);
});
// Let look at the air (by default, once WebGPU is found off touch devices), the model starts
// loading behind the clay being kneaded.
effect(() => {
  if (settings.atmosphere.value) prepareAtmosphere();
});
const work = await loadWork().catch(() => null);
const resumed = work && REQUESTS.find((r) => r.id === work.request);
const resumedFree = work && CLAY_KINDS.find((kind) => work.request === FREE + kind);
// The work kept (or else the first request) is made ready behind the start page; the others
// are meshed as they are taken on. Nothing in the workshop is gone to meanwhile.
workshop.active = false;
const first = resumed
  ? loadStage(resumed)
  : resumedFree
  ? loadClay(resumedFree)
  : loadStage(REQUESTS[0]);
// The workshop is drawn meanwhile: at the title, nothing waits for a stage.
requestAnimationFrame(frame);
/** Picks the work kept up where it was, once made ready. */
const takeWork = () => resumed ? takeRequest(resumed, work!) : takeFree(resumedFree!, work!);
if (work && (resumed || resumedFree) && work.paused) {
  // Reloaded at the title screen, the clay left kneading: the title again, offering it.
  await first;
  takeWork();
  title.resume.value = leftKneading();
  refreshTools();
} else if (work && (resumed || resumedFree)) {
  // Reloaded while kneading: straight back to the clay.
  title.shown.value = false;
  await whileLoading(async () => {
    await first;
    takeWork();
    leaveTitle();
    prepareAtmosphere();
  });
  void showWorkGuide(resumed?.id ?? "");
} else {
  // The title screen, with the first request made ready meanwhile.
  await first;
  takeRequest(REQUESTS[0]);
  refreshTools();
}
// Ready: the start page can be left for the title screen (or the clay), and the workshop (if
// shown) can be gone about.
start.ready.value = true;
if (title.shown.value) workshop.active = true;
